import dayjs from 'dayjs';
import { watch } from 'vue';
import { beautySlug } from '@/store/utils';

const RECENT_TASKS_LIMIT = 5;
const SYNC_DELAY_MS = 250;
const REFRESH_REPORTS_INTERVAL_MS = 60 * 60 * 1000;

function cleanText(value, fallback = '') {
  const text = String(value || fallback).replace(/\s+/g, ' ').trim();
  return text || fallback;
}

function limitText(value, maxLength) {
  const text = cleanText(value);
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 3)}...`;
}

function encodePathSegment(value) {
  return encodeURIComponent(String(value));
}

function getTaskProject(task, store) {
  if (task?.project?.slug) {
    return task.project;
  }

  const currentProject = store.state.project.project;
  if (currentProject?.id === task?.projectId && currentProject.slug) {
    return currentProject;
  }

  return store.state.project.projectsList?.find(project => project.id === task?.projectId);
}

function buildTaskPath(task, store) {
  const project = getTaskProject(task, store);
  if (!task?.hid || !task?.slug || !project?.slug) {
    return null;
  }

  const projectNameSlug = project.nameSlug || beautySlug({ text: project.name || project.slug }, 'project');
  const taskNameSlug = beautySlug({
    text: task.textMD || 'image',
    lower: true,
    length: 30,
    sliceLength: 15,
  }, 'untitled-task');

  return [
    '',
    'project',
    project.slug,
    projectNameSlug,
    task.hid,
    task.slug,
    taskNameSlug,
  ].map((segment, index) => (index === 0 ? segment : encodePathSegment(segment))).join('/');
}

function buildTaskTitle(task, store) {
  const project = getTaskProject(task, store);
  const projectName = cleanText(project?.name, 'Project');
  const taskHid = cleanText(task?.hid, '?');
  const taskTitle = cleanText(task?.textMD, 'Untitled task');
  return limitText(`${projectName}/${taskHid}: ${taskTitle}`, 120);
}

function getAvatarUrl(user) {
  if (!user?.id || !user.avatarFileName) {
    return null;
  }

  const avatarFileName = String(user.avatarFileName);
  if (/^https?:\/\//i.test(avatarFileName)) {
    return avatarFileName;
  }

  const fileName = /\.(png|jpe?g|webp)$/i.test(avatarFileName)
    ? avatarFileName
    : `${avatarFileName}.png`;

  return `${window.s3base}/useravatars/${user.id}/${fileName}`;
}

function getAssignedUser(task, store) {
  if (!task?.assigneeId) {
    return null;
  }

  const user = store.state.user.workspace?.users?.find(usr => usr.id === task.assigneeId);
  return {
    id: task.assigneeId,
    avatarFileName: user?.avatarFileName || null,
    avatarUrl: getAvatarUrl(user),
  };
}

function normalizeTask(task, store) {
  const path = buildTaskPath(task, store);
  if (!path) {
    return null;
  }

  return {
    id: task.id || task.slug,
    title: buildTaskTitle(task, store),
    path,
    assignedUser: getAssignedUser(task, store),
  };
}

function buildMyHoursThisMonthPath(user) {
  const dateFrom = dayjs().startOf('month').format('YYYY-MM-DD');
  const dateTo = dayjs().format('YYYY-MM-DD');
  return `/tasks-time-spent/?dates=${dateFrom},${dateTo}&executors=${encodeURIComponent(user.id)}&mode=hours&groupBy=None`;
}

function buildReports(user) {
  if (!user?.id) {
    return [];
  }

  return [
    {
      id: 'my-hours-this-month',
      title: 'My hours this month',
      path: buildMyHoursThisMonthPath(user),
    },
    {
      id: 'workspace-summary',
      title: 'Summary',
      path: '/workspace-summary/',
    },
  ];
}

function buildPayload(store) {
  const user = store.state.user.user;
  if (!user?.id) {
    return {
      userId: null,
      currentTask: null,
      recentTasks: [],
      reports: [],
    };
  }

  const openedTask = normalizeTask(store.state.project.taskUnderEdit, store);
  const trackingTask = normalizeTask(store.state.task.activeTrackingTask, store);
  const currentTask = openedTask || trackingTask;
  const recentTasks = (store.state.user.recentTasks || [])
    .map(task => normalizeTask(task, store))
    .filter(task => task && task.id !== currentTask?.id)
    .slice(0, RECENT_TASKS_LIMIT);

  return {
    userId: user.id,
    currentTask,
    recentTasks,
    reports: buildReports(user),
  };
}

export function startElectronJumpListSync(store) {
  if (!window.APP_TRON || !window.isMainWindow) {
    return () => {};
  }

  const { ipcRenderer } = window.APP_TRON;
  let updateTimer = null;
  let lastPayloadJson = '';

  const sendUpdate = () => {
    const payload = buildPayload(store);
    const payloadJson = JSON.stringify(payload);
    if (payloadJson === lastPayloadJson) {
      return;
    }

    lastPayloadJson = payloadJson;
    ipcRenderer.send('jumpList:update', payload);
  };

  const scheduleUpdate = () => {
    if (updateTimer) {
      clearTimeout(updateTimer);
    }
    updateTimer = setTimeout(sendUpdate, SYNC_DELAY_MS);
  };

  const unwatch = watch(
    () => buildPayload(store),
    scheduleUpdate,
    { deep: true, immediate: true },
  );

  const reportRefreshTimer = setInterval(scheduleUpdate, REFRESH_REPORTS_INTERVAL_MS);

  return () => {
    unwatch();
    clearInterval(reportRefreshTimer);
    if (updateTimer) {
      clearTimeout(updateTimer);
    }
  };
}
